import { useState } from 'react';
import { Link } from 'react-router-dom';

interface PortfolioItem {
  src: string;
  alt: string;
  category: string;
}

const ITEMS: PortfolioItem[] = [
  {
    src: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&h=600&q=75&auto=format&fit=crop',
    alt: 'Contemporary luxury living room',
    category: 'Living Room',
  },
  {
    src: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=600&q=75&auto=format&fit=crop',
    alt: 'Luxury kitchen design',
    category: 'Kitchen',
  },
  {
    src: 'https://images.unsplash.com/photo-1540518614846-7eded433c457?w=600&h=900&q=75&auto=format&fit=crop',
    alt: 'Elegant master bedroom',
    category: 'Bedroom',
  },
  {
    src: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800&h=600&q=75&auto=format&fit=crop',
    alt: 'Luxury spa bathroom',
    category: 'Bathroom',
  },
  {
    src: 'https://images.unsplash.com/photo-1617806118233-18e1de247200?w=600&h=600&q=75&auto=format&fit=crop',
    alt: 'Formal dining room',
    category: 'Dining Room',
  },
  {
    src: 'https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=600&h=900&q=75&auto=format&fit=crop',
    alt: 'Refined home office',
    category: 'Home Office',
  },
  {
    src: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&q=75&auto=format&fit=crop',
    alt: 'Open plan luxury interior',
    category: 'Interior',
  },
  {
    src: 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=600&h=600&q=75&auto=format&fit=crop',
    alt: 'Elegant nursery design',
    category: 'Nursery',
  },
  {
    src: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800&h=600&q=75&auto=format&fit=crop',
    alt: 'Beverly Hills exterior',
    category: 'Exterior',
  },
];

const CATEGORIES = ['All', 'Living Room', 'Kitchen', 'Bedroom', 'Bathroom', 'Dining Room', 'Home Office', 'Interior', 'Nursery', 'Exterior'];

function Card({ item, index }: { item: PortfolioItem; index: number }) {
  const [loaded, setLoaded] = useState(false);
  const isWide = index === 0 || index === 4 || index === 7;
  const isTall = index === 2 || index === 6;

  return (
    <div
      className={`relative overflow-hidden group cursor-pointer ${
        isWide ? 'col-span-8' : isTall ? 'col-span-4 row-span-2' : 'col-span-4'
      }`}
    >
      {!loaded && <div className="absolute inset-0 bg-[#ddd5c8] animate-pulse z-10" />}
      <img
        src={item.src}
        alt={item.alt}
        loading={index < 4 ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={`w-full h-full object-cover object-center transition-all duration-700 group-hover:scale-105 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300" />
      <div className="absolute bottom-0 left-0 right-0 p-5 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
        <p className="text-white text-xs font-sans font-medium">{item.alt}</p>
        <p className="text-white/60 text-[10px] font-sans mt-0.5 tracking-wider uppercase">{item.category}</p>
      </div>
    </div>
  );
}

export default function PortfolioSection() {
  const [filter, setFilter] = useState('All');
  const visible = filter === 'All' ? ITEMS : ITEMS.filter((i) => i.category === filter);

  return (
    <section id="portfolio" className="bg-[#F9F6F1] py-28">
      <div className="max-w-[1400px] mx-auto px-8">

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">Portfolio</span>
            <h2 className="font-display text-5xl lg:text-6xl font-light text-charcoal mt-3 leading-tight">
              Latest Projects
            </h2>
            <p className="text-charcoal/50 text-sm font-sans mt-3 max-w-md">
              Residential and commercial design across Beverly Hills, Los Angeles, and beyond.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-1.5 text-[10px] tracking-wider font-sans uppercase rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                  filter === cat
                    ? 'bg-charcoal text-cream border-charcoal'
                    : 'bg-transparent text-charcoal/50 border-charcoal/20 hover:border-charcoal/50 hover:text-charcoal'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-3 auto-rows-[200px]">
          {visible.slice(0, 9).map((item, i) => (
            <Card key={item.src} item={item} index={i} />
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/about"
            className="inline-flex items-center gap-2 text-charcoal text-[10px] tracking-[0.2em] uppercase font-sans border-b border-charcoal/25 pb-0.5 hover:border-charcoal hover:gap-4 transition-all cursor-pointer"
          >
            Learn About Our Studio <i className="ri-arrow-right-line" />
          </Link>
        </div>

      </div>
    </section>
  );
}
